import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePerm, usePermScope } from '@/lib/perm';
import { useLang } from '@/lib/i18n';
import { api, errMsg } from '@/lib/api';
import { AppShell, EmptyState } from '@/components/AppShell';
import { ModuleIcon } from '@/components/ModuleIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export const idr = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');
export const today = () => new Date().toISOString().slice(0, 10);

/** Company-scoped API + module guard. */
export const useErp = (moduleKey) => {
  const { company, user } = useAuth();
  const { lang } = useLang();
  const perm = usePerm(moduleKey);
  usePermScope(moduleKey);
  const enabled = company?.modules?.find((m) => m.key === moduleKey)?.enabled && perm.canView;
  const base = company ? `/companies/${company.id}` : '';
  const call = useCallback(async (method, path, data) => {
    try { const res = await api[method](`${base}${path}`, data); return res.data; } catch (e) { toast.error(errMsg(e)); throw e; }
  }, [base]);
  return { company, user, lang, id: lang === 'id', enabled, call, base, ...perm };
};

export const ErpShell = ({ moduleKey, title, enabled, company, actions, children, lang }) => {
  const navigate = useNavigate();
  if (!company) return <AppShell><div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" /></div></AppShell>;
  if (!enabled && company.permissions && !['owner', 'admin'].includes(company.role) && company.permissions[moduleKey] === 'no') return <AppShell moduleName={title} moduleIcon={<ModuleIcon moduleKey={moduleKey} size="sm" />}><div className="mx-auto max-w-xl px-4 py-16" data-testid={`${moduleKey}-no-access`}><EmptyState title={lang === 'id' ? 'Tidak ada akses' : 'No access'} description={lang === 'id' ? `Anda tidak memiliki izin untuk modul ${title}. Hubungi administrator perusahaan.` : `You have no permission for ${title}. Ask your company administrator.`} /></div></AppShell>;
  if (!enabled) return <AppShell moduleName={title} moduleIcon={<ModuleIcon moduleKey={moduleKey} size="sm" />}><div className="mx-auto max-w-xl px-4 py-16"><EmptyState title={lang === 'id' ? `Modul ${title} belum diinstal` : `${title} module not installed`} description={lang === 'id' ? 'Instal melalui menu Aplikasi.' : 'Install it from the Apps menu.'} action={<Button className="rounded-xl" onClick={() => navigate('/modules')} data-testid={`${moduleKey}-go-modules`}>{lang === 'id' ? 'Buka Aplikasi' : 'Open Apps'}</Button>} /></div></AppShell>;
  return <AppShell moduleName={title} moduleIcon={<ModuleIcon moduleKey={moduleKey} size="sm" />} actions={actions && <span data-perm-write className="flex min-w-0 items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0">{actions}</span>}><div className="mx-auto max-w-7xl px-4 sm:px-6 py-6">{children}</div></AppShell>;
};

export const SubTabs = ({ tabs, value, onChange, testid = 'subtabs' }) => (
  <div className="mb-5 flex gap-1 overflow-x-auto rounded-xl border border-border bg-white p-1 w-fit max-w-full" data-testid={testid}>
    {tabs.map((t) => <button type="button" key={t.key} onClick={() => onChange(t.key)} {...(t.perm ? { [`data-perm-${t.perm}`]: true } : {})} className={`whitespace-nowrap rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors ${value === t.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`} data-testid={`${testid}-${t.key}`}>{t.label}</button>)}
  </div>
);

export const Stat = ({ label, value, tone = '', testid }) => (
  <div className="rounded-2xl border border-border bg-white px-4 py-3 shadow-soft" data-testid={testid}><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div><div className={`mt-0.5 font-display text-lg font-semibold ${tone}`}>{value}</div></div>
);

export const Grid = ({ cols, rows, empty, rowKey = (r) => r.id, onRow, testid = 'grid' }) => (
  <div className="overflow-x-auto rounded-2xl border border-border bg-white shadow-soft" data-testid={testid}>
    <Table>
      <TableHeader><TableRow>{cols.map((c) => <TableHead key={c.key} className={c.right ? 'text-right' : ''}>{c.label}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {rows.length === 0 && <TableRow><TableCell colSpan={cols.length} className="py-10 text-center text-sm text-muted-foreground">{empty}</TableCell></TableRow>}
        {rows.map((r) => <TableRow key={rowKey(r)} className={onRow ? 'cursor-pointer' : ''} onClick={() => onRow?.(r)} data-testid={`${testid}-row-${rowKey(r)}`}>{cols.map((c) => <TableCell key={c.key} className={`text-sm ${c.right ? 'text-right whitespace-nowrap' : ''}`}>{c.render ? c.render(r) : r[c.key]}</TableCell>)}</TableRow>)}
      </TableBody>
    </Table>
  </div>
);

export const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">{label}</Label>{children}</div>;

export const Pick = ({ value, onChange, options, placeholder, testid, allowNone = true }) => (
  <Select value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? '' : v)}>
    <SelectTrigger className="rounded-xl" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-64">{allowNone && <SelectItem value="none">{placeholder || '—'}</SelectItem>}{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
  </Select>
);

export const FormDialog = ({ open, onClose, title, desc, onSubmit, children, submitLabel, busy, testid = 'form-dialog', wide, footerExtra }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className={wide ? 'sm:max-w-3xl max-h-[90vh] overflow-y-auto' : 'sm:max-w-lg max-h-[90vh] overflow-y-auto'} data-testid={testid}>
      <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle><DialogDescription>{desc || ' '}</DialogDescription></DialogHeader>
      <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); onSubmit(); }} className="space-y-4 min-w-0">
        {children}
        <DialogFooter><Button type="button" variant="outline" className="rounded-xl" onClick={onClose}>Batal</Button>{footerExtra}<Button type="submit" className="rounded-xl" disabled={busy} data-perm-write data-testid={`${testid}-submit`}>{busy ? <Loader2 size={16} className="animate-spin" /> : submitLabel || 'Simpan'}</Button></DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const NumInput = (props) => <Input type="number" inputMode="decimal" className="rounded-xl" {...props} />;

export const useLoader = (fn, deps) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => fn().then(setData).catch(() => {}), deps); // eslint-disable-line
  useEffect(() => { setData(null); load(); }, [load]);
  return [data, load];
};
